import { useMemo } from "react";
import moment from "moment";
import { OrgField } from "@ollie-sports/models";
import { api } from "@ollie-sports/core";
import { translate } from "@ollie-sports/i18n";

/**
 * Warns when a field is marked closed, or has a block, during an event's time window.
 * Used on the event add/edit form and the event detail page. Non-blocking: org admins
 * can still save (they may be the ones who closed it and know better).
 */
export function FieldClosedWarning(p: {
  orgField?: OrgField;
  startDateTime?: string;
  endDateTime?: string;
  isAllDay?: boolean;
  className?: string;
}) {
  const closure = useMemo(
    () =>
      p.orgField && p.startDateTime && p.endDateTime
        ? api.getFieldClosureForEvent(p.orgField, p.startDateTime, p.endDateTime, p.isAllDay)
        : undefined,
    [p.orgField, p.startDateTime, p.endDateTime, p.isAllDay]
  );

  if (!closure || !p.orgField) return null;

  const dateLabel = moment(closure.dateString, "YYYY-MM-DD").format("ddd, MMM D");

  return (
    <div className={`bg-red-50 border border-red-200 rounded px-3 py-2 text-sm ${p.className ?? ""}`}>
      <div className="font-semibold text-red-800 mb-1">{translate({ defaultMessage: "Field Unavailable" })}</div>
      <div className="text-red-700">
        {closure.type === "closed"
          ? translate(
              { defaultMessage: "{fieldName} is marked closed on {date}." },
              { fieldName: p.orgField.name, date: dateLabel }
            )
          : translate(
              { defaultMessage: "{fieldName} is blocked {startTime} - {endTime} on {date}: {reason}" },
              {
                fieldName: p.orgField.name,
                date: dateLabel,
                startTime: moment(closure.block.startTime, "HH:mm").format("h:mm A"),
                endTime: moment(closure.block.endTime, "HH:mm").format("h:mm A"),
                reason: closure.block.reason
              }
            )}
      </div>
    </div>
  );
}
